import { Layout } from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import AddressCard from './components/AddressCard';
import ArticleCard from './components/ArticleCard';
import CategoryCard from './components/CategoryCard';
import { addresses, stats } from '@/mocks/addresses';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';

export default function HomePage() {
  const primaryAddresses = addresses.filter((a) => a.type === 'primary' || (a.type === 'backup' && a.badge));
  const backupAddresses = addresses.filter((a) => a.type === 'backup' && !a.badge);
  const emergencyAddr = addresses.find((a) => a.type === 'emergency');
  const latestArticles = articles.slice(0, 6);

  const catArticleCounts: Record<string, number> = {};
  articles.forEach((a) => {
    catArticleCounts[a.category] = (catArticleCounts[a.category] || 0) + 1;
  });

  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <Layout>
      <SEOMeta
        title="地址发布页 - 官方最新入口导航 | 永久地址发布 安全稳定访问"
        description="官方地址发布页，提供最新可用访问入口与备用地址导航。收藏本地址发布页永不迷路，第一时间获取最新地址更新通知，安全稳定访问无忧。"
        keywords="地址发布页,官方地址发布页,最新地址入口,备用地址导航,地址更新通知,永久地址发布,最新可用链接"
        canonicalUrl="https://gtucxr.cn/"
        ogType="website"
      />

      <section className="relative pt-16">
        <div className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20abstract%20geometric%20pattern%20with%20warm%20golden%20amber%20and%20cream%20tones%2C%20clean%20smooth%20gradient%20background%20with%20soft%20curved%20lines%2C%20subtle%20texture%2C%20professional%20corporate%20style%2C%20bright%20airy%20atmosphere%2C%20no%20text%2C%20no%20people%2C%204K%20quality&width=1800&height=900&seq=hero-home-bg-2026&orientation=landscape"
            alt="地址发布页官方入口导航背景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/25"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center max-w-3xl w-full">
              <div className="inline-flex items-center gap-2 bg-background-50/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
                <span className="text-xs text-background-50 font-medium">所有节点运行正常 · 最后更新: 2026-08-06</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
                官方地址发布页
              </h1>
              <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-xl mx-auto leading-relaxed">
                官方唯一指定地址入口导航平台。收藏本页，获取最新可用地址，永不迷路。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={primaryAddresses[0]?.url || '#'}
                  className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  访问主站
                </a>
                <a
                  href="#addresses"
                  className="border border-background-200/40 text-background-50 hover:bg-background-50/10 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-down-line mr-2"></i>
                  查看所有地址
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200">
              <p className="text-2xl font-heading font-bold text-primary-500">{(stats.totalVisits / 10000).toFixed(0)}万+</p>
              <p className="text-xs text-foreground-500 mt-1">累计访问量</p>
            </div>
            <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200">
              <p className="text-2xl font-heading font-bold text-accent-500">{stats.onlineNodes}</p>
              <p className="text-xs text-foreground-500 mt-1">在线节点数</p>
            </div>
            <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200">
              <p className="text-2xl font-heading font-bold text-primary-500">{stats.uptime}</p>
              <p className="text-xs text-foreground-500 mt-1">服务可用率</p>
            </div>
            <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200">
              <p className="text-2xl font-heading font-bold text-accent-500">{stats.onlineDays}</p>
              <p className="text-xs text-foreground-500 mt-1">稳定运行天数</p>
            </div>
            <div className="bg-background-50 rounded-xl p-4 text-center border border-background-200">
              <p className="text-2xl font-heading font-bold text-primary-500">{(stats.usersServed / 10000).toFixed(1)}万+</p>
              <p className="text-xs text-foreground-500 mt-1">服务用户数</p>
            </div>
          </div>
        </div>
      </section>

      <section id="addresses" className="py-14 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              最新官方地址入口
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              以下为官方最新可用访问地址，建议优先使用推荐节点，如遇访问问题可尝试备用地址
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-sm font-heading font-semibold text-foreground-700 mb-4 flex items-center gap-2">
              <i className="ri-star-line text-primary-500"></i>
              推荐入口
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-product-shop="">
              {primaryAddresses.map((addr, idx) => (
                <AddressCard key={addr.id} {...addr} index={idx} />
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-sm font-heading font-semibold text-foreground-700 mb-4 flex items-center gap-2">
              <i className="ri-node-tree text-accent-500"></i>
              区域备用节点
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop="">
              {backupAddresses.map((addr, idx) => (
                <AddressCard key={addr.id} {...addr} index={idx} />
              ))}
            </div>
          </div>

          {emergencyAddr && (
            <div>
              <h3 className="text-sm font-heading font-semibold text-foreground-700 mb-4 flex items-center gap-2">
                <i className="ri-alert-line text-red-500"></i>
                紧急备用
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop="">
                <AddressCard {...emergencyAddr} index={0} />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-14 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              内容分类导航
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              按分类浏览文章，快速找到您需要的信息
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <CategoryCard
                key={cat.slug}
                {...cat}
                count={catArticleCounts[cat.slug] || 0}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                最新文章
              </h2>
              <p className="text-sm text-foreground-600">
                最新的官方公告、使用教程与安全指南
              </p>
            </div>
            <Link
              to="/category/zixun"
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latestArticles.map((article) => (
              <ArticleCard
                key={article.slug}
                slug={article.slug}
                title={article.title}
                excerpt={article.excerpt}
                categoryName={article.categoryName}
                category={article.category}
                date={article.date}
                readTime={article.readTime}
                views={article.views}
              />
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/zixun"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部文章
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
            订阅地址更新通知
          </h2>
          <p className="text-sm text-background-200/80 mb-8 max-w-md mx-auto">
            第一时间获取最新地址变更通知，确保您始终使用正确的访问入口
          </p>

          <form
            onSubmit={handleSubscribe}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入您的邮箱地址"
              required
              className="w-full sm:flex-1 px-4 py-3 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm border-0 outline-none focus:ring-2 focus:ring-background-300"
            />
            <button
              type="submit"
              className="w-full sm:w-auto bg-foreground-900 hover:bg-foreground-800 text-background-50 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {subscribed ? (
                <>
                  <i className="ri-check-line mr-1.5"></i>
                  订阅成功
                </>
              ) : (
                <>
                  <i className="ri-mail-line mr-1.5"></i>
                  立即订阅
                </>
              )}
            </button>
          </form>

          <p className="mt-3 text-xs text-background-200/60">
            我们承诺不会发送垃圾邮件，您可以随时取消订阅
          </p>

          <div className="hp-field-wrap">
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '地址发布页',
            url: 'https://gtucxr.cn/',
            description: '官方地址发布页，提供最新可用访问入口与备用地址导航',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'support@official-site.com',
            },
          }),
        }}
      />
    </Layout>
  );
}